import React, { useState, useEffect, useRef } from 'react';
import { PostRequest } from 'components/common/index.js';
import fileDownload from 'js-file-download'
import Constants from 'components/show_drawing/Constants.jsx';
import BotChallenge from 'components/common/BotChallenge.jsx'
import ConfirmEmailWithCode from 'components/ConfirmEmailWithCode.jsx';

const TABLE_OUTER_STYLE = { border: '1px solid #ddd' };
const TABLE_CELL_STYLE  = { border: '1px solid #ddd', padding: '8px' };
const COMBO_SECTION_STYLE = { marginTop: '10px' };
const COMBO_TITLE_STYLE = { marginBottom: '4px' };
const COMBO_SUBTITLE_STYLE = { color: '#888', marginLeft: '8px' };

// Reads the current values of any `ask_values[<relation>][<name>]` and
// `combo_materials[<relation>][<field>]` inputs (rendered by
// ComboButtonsSection / ComboGuestMaterialChooser) plus the top-level
// hidden `combo_ids` field, and returns them nested the way Rails would
// parse a form-encoded request. Callers merge the result into the JSON POST
// body so params[:ask_values] / params[:combo_materials] land in the shape
// BuildComboGuestInputsService expects.
const readComboStateFromForm = (formRef) => {
  const result = {};
  if (!formRef || !formRef.current) { return result; }

  const formData = new FormData(formRef.current);
  const askValues = {};
  const comboMaterials = {};

  for (const [key, value] of formData.entries()) {
    if (key === 'combo_ids') {
      // RedrawForm.jsx renders an empty combo_ids input when no combo is
      // active; skip that so the download body stays clean.
      if (value !== '') { result.combo_ids = value; }
      continue;
    }
    const askMatch = key.match(/^ask_values\[([^\]]+)\]\[([^\]]+)\]$/);
    if (askMatch) {
      const [, relationId, optionName] = askMatch;
      askValues[relationId] = askValues[relationId] || {};
      askValues[relationId][optionName] = value;
      continue;
    }
    const matMatch = key.match(/^combo_materials\[([^\]]+)\]\[([^\]]+)\]$/);
    if (matMatch) {
      const [, relationId, fieldName] = matMatch;
      comboMaterials[relationId] = comboMaterials[relationId] || {};
      comboMaterials[relationId][fieldName] = value;
    }
  }

  if (Object.keys(askValues).length > 0)      { result.ask_values = askValues; }
  if (Object.keys(comboMaterials).length > 0) { result.combo_materials = comboMaterials; }
  return result;
};

const DownloadConfirmation = ( { id, drawing, currentDrawingOptions, currentUserDrawingOptions, advancedOptionsVisible, fileFormat, dimensionTextschecked, comboEnabled, formRef } ) => {
  const [canDownload, setCanDownload] = useState(true)
  // Starts true so the confirm-email modal never flashes before the first status
  // fetch. A signed-in user's confirmation can be revoked (an SES bounce report on
  // the confirmation email) between the download button rendering and this popup
  // opening, which is the case this drives.
  const [confirmed, setConfirmed] = useState(true)
  const [presentedOptions, setPresentedOptions] = useState( {} )
  // The catalog name of the technical property combination being downloaded,
  // which may be one of its own rather than the drawing's.
  const [catalogNameAsTitle, setCatalogNameAsTitle] = useState(drawing.catalog_name_as_title)
  const notesTextRef = useRef();
  const [fileName, setFileName] = useState('')
  const [defaultFileName, setDefaultFileName] = useState('')
  const [fileNameWithDimensions, setFileNameWithDimensions] = useState('')
  const [fileNameWithDimensionsChecked, setFileNameWithDimensionsChecked] = useState(false)
  const [joinPathsChecked, setJoinPathsChecked] = useState(false)
  // The PDF line thickness field (default, range and unit) from params_to_confirm; null until it answers.
  const [strokeWidthField, setStrokeWidthField] = useState(null)
  const [strokeWidthValue, setStrokeWidthValue] = useState('')
  const strokeWidthInputRef = useRef();
  const [downloadRefused, setDownloadRefused] = useState(false)
  const [notesVisible, setNotesVisible] = useState(false)
  const [charRemaining, setCharRemaining] = useState(255)
  const [cadetClassName, setCadetClassName] = useState("fa fa-caret-down")
  const [downloadWillCostCredit, setDownloadWillCostCredit] = useState(false)
  const [creditCount, setCreditCount] = useState(0)
  const [couponCode, setCouponCode] = useState("")
  const [downloadWillCostCoupon, setDownloadwillCostCouponCode] = useState(false)
  const [alreadyDownloaded, setAlreadyDownloaded] = useState(false)
  const [activeSubscription, setActiveSubscription] = useState(false)
  const [downloadNotes, setDownloadNotes] = useState("")
  const [loading, setLoading] = useState(false)
  const [comboSections, setComboSections] = useState([])
  // Set to the reCAPTCHA site key when the server challenges a level-2 suspected bot; the
  // challenge then renders in the download form in place of the download button.
  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState(null)

  const getPresentedOptions = () => {
    setLoading( () => true )
    // /Users/emre/code/boxes/app/controllers/concerns/show_drawing_react_actions.rb
    const data = { ...currentUserDrawingOptions };
    if (comboEnabled) {
      Object.assign(data, readComboStateFromForm(formRef));
    }
    PostRequest({
      url: "/" + id  +  `/params_to_confirm?advanced_options_visible=${advancedOptionsVisible}`,
      data: data,
      callback: (data) => {
        setPresentedOptions( () => data.options_to_confirm )
        setCatalogNameAsTitle( () => data.catalog_name_as_title )
        setComboSections( () => data.combo_sections || [] )
        setCanDownload( () => data.can_download )
        setConfirmed( () => data.confirmed )
        setFileName( () => data.default_file_name )
        setDefaultFileName( () => data.default_file_name )
        setFileNameWithDimensions( () => data.file_name_with_dimensions )
        setDownloadWillCostCredit( () => data.download_will_cost_credit )
        setCreditCount( () => data.available_credits )
        setDownloadwillCostCouponCode( () => data.download_will_cost_coupon_code )
        setCouponCode( () => data.coupon_code_value )
        setAlreadyDownloaded( () => data.already_downloaded )
        setActiveSubscription( () => data.active_subscription )
        setStrokeWidthField( () => data.stroke_width )
        setLoading( () => false )
      }
    })
  }

  const handleFileDimensionsChecked = (e) => {
    setFileNameWithDimensionsChecked( (prev) => !prev )
  }

  const handleJoinPathsChecked = (e) => {
    setJoinPathsChecked( (prev) => !prev )
  }

  const handleAddNotesClick = (e) => {
    e.preventDefault()
    setNotesVisible( (prev) => !prev )
  }

  const handleNotesChange = (e) => {
    var tval = e.target.value,
    tlength = tval.length,
    set = 254,
    remain = parseInt(set - tlength);
    setCharRemaining( () => remain )

    if (remain <= 0 && e.which !== 0 && e.charCode !== 0) {
      e.target.value = (tval).substring(0, tlength - 1);
      return false;
    }

    setDownloadNotes( () => e.target.value )
  }

  const strokeWidthOffered = fileFormat === 'pdf' && strokeWidthField !== null
  const strokeWidthUnit = strokeWidthField === null ? null : strokeWidthField.unit
  // Confirm shows while params_to_confirm is loading, and the server refuses a PDF without its line thickness.
  const pdfWaitingForStrokeWidth = fileFormat === 'pdf' && strokeWidthField === null

  const strokeWidthOnChange = (e) => {
    setStrokeWidthValue( () => e.target.value )
  }

  const fileNameOnChange = (e) => {
    setFileName( () => e.target.value )
  }
  const fileNameWithExtension = () => {
    return `${fileName}.${fileFormat}`
  }

  const performDownload = (e, recaptchaToken) => {
    if (e && e.preventDefault) { e.preventDefault(); }
    setDownloadRefused(false);

    // The download starts from a button rather than a form submit, so the browser checks the range only when asked.
    if (strokeWidthOffered && !strokeWidthInputRef.current.reportValidity()) { return; }

    // Only the initial attempt starts the funnel; a reCAPTCHA-challenge retry (which
    // carries a token) is the same download intent and must not double-count.
    if (!recaptchaToken) {
      emitEvent(Constants.drawing_download_started, {id: id})
    }

    const url = `/download/${id}/${fileFormat}`
    const dataToPost = { ...currentUserDrawingOptions };
    dataToPost.authenticity_token = $('meta[name=csrf-token]').attr('content');
    dataToPost.dimension_texts_included = dimensionTextschecked
    dataToPost.download_notes = downloadNotes
    dataToPost.join_paths = joinPathsChecked
    if (strokeWidthOffered) {
      dataToPost.stroke_width = strokeWidthValue
      dataToPost.stroke_width_unit = strokeWidthField.unit
    }

    // A level-2 suspected bot must carry a fresh reCAPTCHA token (obtained from the popup
    // shown on the 403 below) or the server challenges the download again.
    if (recaptchaToken) {
      dataToPost['g-recaptcha-response'] = recaptchaToken;
    }

    // Work 08: carry active combo state in the download body so the server can
    // produce a combined file. These keys aren't part of currentUserDrawingOptions
    // because they bypass formula_parameters in formula_option_actions.rb.
    if (comboEnabled) {
      Object.assign(dataToPost, readComboStateFromForm(formRef));
    }

    const options = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dataToPost)
    };

    fetch(url, options)
      .then( async res => {
        // The server flagged this account as a level-2 suspected bot and returned a
        // reCAPTCHA challenge instead of the file. Render the challenge in the form (in
        // place of the download button) and skip the blob save so a JSON challenge body is
        // never written as a corrupt file. Parse defensively: the blacklisted-IP gate can
        // also answer 403 with an HTML page, which must still fall through to the normal
        // (blob) handling below.
        if (res.status === 403) {
          let data = null;
          try { data = await res.clone().json(); } catch (error) { data = null; }
          if (data && data.recaptcha_required) {
            setRecaptchaSiteKey(data.recaptcha_site_key);
            return;
          }
        }

        // The server refused the download; its empty body must not be saved as the file.
        if (res.status === 422) {
          trackEvent('js_error_happened', { source: 'DownloadConfirmation download refused', id: id, status: res.status })
          setDownloadRefused(true);
          return;
        }

        const blob = await res.blob();
        emitEvent(Constants.drawing_download_finished, {id: id})
        fileDownload(blob, fileNameWithExtension())
        refreshDownloadCounts();
      });
  }

  // The user solved the in-form reCAPTCHA: remove it and re-issue the download carrying
  // the token so the server serves the file this time.
  const onChallengeVerified = (token) => {
    setRecaptchaSiteKey(null);
    performDownload(null, token);
  }

  // The user typed the correct confirmation code inside this popup: re-fetch so
  // can_download flips to true and the download form replaces the confirm modal,
  // and refresh the button section behind the popup with the new status.
  const handleConfirmed = () => {
    getPresentedOptions();
    refreshDownloadButtons();
  }

  const downloadForm = () => {
    return <div>
      <style>{`
        .dl-modern__card { border:1px solid #e6e6e6; border-radius:12px; padding:22px 24px; background:#fff; box-shadow:0 1px 2px rgba(0,0,0,.04); }
        .dl-modern__heading { margin:0 0 12px; font-size:13px; font-weight:600; color:var(--ink-soft); }
        .dl-modern__divider { margin:20px 0 16px; border:0; border-top:1px solid var(--rule); }
        .dl-modern__setting { display:flex; align-items:center; gap:10px; margin:14px 0 0; font-size:14px; font-weight:400; color:#2b2b2b; }
        .dl-modern__setting input { width:90px; border:1px solid #d9d9d9; border-radius:8px; padding:7px 10px; font-size:14px; color:#222; }
        .dl-modern__setting input:focus { outline:none; border-color:#eb5424; box-shadow:0 0 0 3px rgba(235,84,36,.15); }
        .dl-modern__setting-unit { color:#9a9a9a; font-weight:600; }
        .dl-modern__check { display:flex; align-items:flex-start; gap:12px; cursor:pointer; font-size:14px; line-height:1.45; color:#2b2b2b; user-select:none; margin:0; font-weight:400; max-width:100%; }
        .dl-modern__check input { appearance:none; -webkit-appearance:none; width:20px; height:20px; margin:0; flex:0 0 auto; border:1.5px solid #cbcbcb; border-radius:6px; background:#fff; cursor:pointer; position:relative; transition:background .15s, border-color .15s; }
        .dl-modern__check input:hover { border-color:#eb5424; }
        .dl-modern__check input:checked { background:#eb5424; border-color:#eb5424; }
        .dl-modern__check input:checked::after { content:''; position:absolute; left:6.5px; top:3px; width:4px; height:9px; border:solid #fff; border-width:0 2px 2px 0; transform:rotate(45deg); }
        .dl-modern__check input:focus-visible { outline:none; box-shadow:0 0 0 3px rgba(235,84,36,.25); }
        .dl-modern__field { display:flex; align-items:stretch; margin-top:10px; border:1px solid #d9d9d9; border-radius:10px; overflow:hidden; background:#fff; transition:border-color .15s, box-shadow .15s; }
        .dl-modern__field:focus-within { border-color:#eb5424; box-shadow:0 0 0 3px rgba(235,84,36,.15); }
        .dl-modern__field-label { padding:0 15px; display:flex; align-items:center; background:#f7f7f7; color:#555; font-weight:600; font-size:13px; white-space:nowrap; border-right:1px solid #ececec; }
        .dl-modern__field input { flex:1 1 auto; min-width:0; border:0; outline:0; padding:12px 15px; font-size:15px; color:#222; background:transparent; }
        .dl-modern__field-ext { padding:0 16px; display:flex; align-items:center; background:#f7f7f7; color:#9a9a9a; font-size:14px; font-weight:600; border-left:1px solid #ececec; }
        .dl-modern__notes { display:inline-flex; align-items:center; gap:6px; margin-top:16px; color:#eb5424; font-size:13px; font-weight:600; text-decoration:none; cursor:pointer; }
        .dl-modern__notes:hover { color:#cf3f12; }
        .dl-modern__textarea { width:100%; margin-top:12px; border:1px solid #d9d9d9; border-radius:10px; padding:10px 12px; font-size:14px; resize:vertical; box-sizing:border-box; }
        .dl-modern__textarea:focus { outline:none; border-color:#eb5424; box-shadow:0 0 0 3px rgba(235,84,36,.15); }
        .dl-modern__char { margin:6px 2px 0; color:#999; font-size:12px; }
        .dl-modern__msg { margin-top:14px; padding:12px 14px; border-radius:10px; background:#fff6f2; border:1px solid #f6d5c7; color:#8a3a1c; font-size:13px; line-height:1.5; }
        .dl-modern__actions { margin-top:22px; text-align:center; }
        .dl-modern__btn { appearance:none; border:0; cursor:pointer; background:#eb5424; color:#fff; font-size:15px; font-weight:600; padding:12px 36px; border-radius:10px; transition:background .15s; }
        .dl-modern__btn:hover { background:#cf3f12; }
      `}</style>

      <div className="dl-modern__card">
        <p className="dl-modern__heading">{ t('download_confirmation.dieline_heading') }</p>
        <label className="dl-modern__check">
          <input type="checkbox" checked={joinPathsChecked} onChange={handleJoinPathsChecked} />
          <span>{ t('download_confirmation.join_paths') }</span>
        </label>

        {strokeWidthOffered && (
          <label className="dl-modern__setting">
            <span>{ t('download_confirmation.stroke_width') }</span>
            <input type="number" step="any" required ref={strokeWidthInputRef}
              min={strokeWidthField.minimum} max={strokeWidthField.maximum}
              value={strokeWidthValue} onChange={strokeWidthOnChange} />
            <span className="dl-modern__setting-unit">{ strokeWidthField.unit }</span>
          </label>
        )}

        <hr className="dl-modern__divider" />
        <p className="dl-modern__heading">{ t('download_confirmation.file_heading') }</p>
        <div>
          <label className="dl-modern__check">
            <input type="checkbox" checked={fileNameWithDimensionsChecked} onChange={handleFileDimensionsChecked} />
            <span>{ t('download_confirmation.file_name_include_dimensions') }</span>
          </label>

          <div className="dl-modern__field">
            <span className="dl-modern__field-label">{ t('download_confirmation.file_name') }</span>
            <input type="text" name="file_name" id="file_name" value={fileName} onChange={fileNameOnChange} />
            <span className="dl-modern__field-ext" id="file_extension">.{fileFormat}</span>
          </div>
        </div>

        <div>
          <a href="#" className="dl-modern__notes" onClick={handleAddNotesClick}>
            { t('download_confirmation.add_notes') } <i className={cadetClassName} aria-hidden="true"></i>
          </a>
        </div>

        {notesVisible && (
          <div>
            <textarea rows="2" className="dl-modern__textarea" onKeyPress={handleNotesChange} ref={notesTextRef}></textarea>
            <p className="dl-modern__char">{ charRemaining }</p>
          </div>
        )}

        {downloadWillCostCredit && <p className="dl-modern__msg"
          dangerouslySetInnerHTML={{__html: t('download_confirmation.credit_information').replace('%{credit_count}', creditCount) }}></p>
        }

        {downloadWillCostCoupon && <p className="dl-modern__msg"
          dangerouslySetInnerHTML={{__html: t('download_confirmation.coupon_information').replace('%{coupon_value}', couponCode) }}></p>
        }

        {alreadyDownloaded && activeSubscription && <p className="dl-modern__msg"
          dangerouslySetInnerHTML={{__html: t('download_confirmation.already_downloaded_without_credit') }}></p>
        }

        {alreadyDownloaded && !activeSubscription && <p className="dl-modern__msg"
          dangerouslySetInnerHTML={{__html: t('download_confirmation.already_downloaded_with_credit') }}></p>
        }

        {downloadRefused && <p className="dl-modern__msg">{ t('download_confirmation.download_refused') }</p>}
      </div>

      <div className="dl-modern__actions">
        {recaptchaSiteKey
          ? <BotChallenge siteKey={recaptchaSiteKey} onVerified={onChallengeVerified} />
          : <button className="dl-modern__btn" onClick={performDownload} disabled={pdfWaitingForStrokeWidth}>{ t('download_confirmation.confirm') }</button>}
      </div>

    </div>
  }

  const cannotDownloadError = () => {
    trackEvent('js_error_download_confirm_but_cannot_download', {
      id: id,
      options: currentDrawingOptions,
    })

    return <div>
      <p> { t('drawing.unexpected_error_happened')  } </p>
    </div>
  }

  const renderOptionsTable = () => {
    if (!presentedOptions || Object.keys(presentedOptions).length === 0) {
      return null;
    }

    return (
      <table className="table table-striped" style={TABLE_OUTER_STYLE}>
        <tbody>
          {Object.entries(presentedOptions).map(([key, value]) => (
            <tr key={key}>
              <td style={TABLE_CELL_STYLE}><strong>{key}</strong></td>
              <td style={TABLE_CELL_STYLE}>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  const renderComboSections = () => {
    if (!comboSections || comboSections.length === 0) { return null; }

    return comboSections.map((section) => (
      <div key={section.relation_id} style={COMBO_SECTION_STYLE}>
        <div style={COMBO_TITLE_STYLE}>
          <strong>{section.guest_name}</strong>
          {section.guest_drawing_name && (
            <small style={COMBO_SUBTITLE_STYLE}>{section.guest_drawing_name}</small>
          )}
        </div>
        <table className="table table-striped" style={TABLE_OUTER_STYLE}>
          <tbody>
            {Object.entries(section.options_to_confirm).map(([label, value]) => (
              <tr key={label}>
                <td style={TABLE_CELL_STYLE}><strong>{label}</strong></td>
                <td style={TABLE_CELL_STYLE}>{String(value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    ));
  }

  // useEffect
  useEffect(getPresentedOptions, [ currentDrawingOptions ]);
  // The confirmation reloads when the options change or the email is confirmed in the popup; that keeps the
  // line thickness the user typed, and only a first field or a different unit starts from its default.
  useEffect( () => {
    if (strokeWidthField !== null) { setStrokeWidthValue( () => strokeWidthField.value ) }
  }, [ strokeWidthUnit ]);
  useEffect( () => {
    setFileName( () => fileNameWithDimensionsChecked ? fileNameWithDimensions : defaultFileName )
  }, [ fileNameWithDimensionsChecked ]);
  useEffect( () => {
    setCadetClassName( () => notesVisible ?  'fa fa-caret-up' : 'fa fa-caret-down' )
    $(notesTextRef.current).focus();
  }, [ notesVisible ] )
  // useEffect

  // Confirmation was revoked after the download button rendered (see the
  // `confirmed` state note): show the confirm-email modal on its own in place of
  // the download form, rather than the generic cannotDownloadError.
  if (!loading && !canDownload && !confirmed) {
    return <ConfirmEmailWithCode onConfirmed={handleConfirmed} />;
  }

  return (
    <div>
      <div className="promo-text">
        { t('download_confirmation.title').replace('%{name}', catalogNameAsTitle) }
      </div>
      <hr />

     <div className="">
      <p>
        { t('download_confirmation.information').replace('%{name}', catalogNameAsTitle) }
      </p>
      </div>

    <div className="">
      {loading && <img src="https://d2atdwxjx7uc4i.cloudfront.net/images/bx_loader.gif" width="30px"/> }
      {!loading && renderOptionsTable()}
      {!loading && renderComboSections()}
      <div style={{marginTop: '15px'}}>
      { canDownload && downloadForm() }
      { !canDownload && cannotDownloadError() }
      </div>
    </div>

  </div>
  )
}

export default DownloadConfirmation;

